/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 15/09/2025 - 11:45:20
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 15/09/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useEffect, useState } from "react";
import { Image, Button, Spinner } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import images from "../../images/index";
import { Link } from "react-router-dom";
import Navbar from "../../components/shared/navbar";
import Footer from "../../components/shared/footer";
import FullReport from "../../components/pages/poFullReport";
import { useLocation, useParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { GoArrowLeft } from "react-icons/go";
import IconWrapper from "../../utils/IconWrapper";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { downloadApi } from "../../api/secureurlapi";
import { toast } from "react-toastify";

interface ReportCardProps {
  date: string;
  fileUrl: {
    paidReportUrl: string;
    reportUrl: string;
  };
  owner: string;
  paymentStatus: string;
  projectName: string;
  tiltedPVArea: number;
  verticalPVArea: number;
  detectionPoints: string;
  id: string;
  projectId: string;
  imageUrl: string;
}

const UnpaidProject: React.FC = () => {
  const { t } = useTranslation(['unpaidProject', 'common']);
  const [reportData, setReportData] = useState<ReportCardProps | null>(null);
  const [secureReportUrl, setSecureReportUrl] = useState<string | null>(null);
  const [isLoadingReport, setIsLoadingReport] = useState<boolean>(true);
  const [isLoadingDownload, setIsLoadingDownload] = useState<boolean>(false);
  const navigate = useNavigate();

  const { projectId } = useParams<{ projectId: string | any }>();
  const location = useLocation();
  const simulationId = location.state?.simulationId;
  const user = auth.currentUser?.uid;
  // Use the simulation ID for fetching the report data
  const docId = simulationId || projectId;

  // Fetch report data from Firestore
  useEffect(() => {
    if (!docId) return;
    
    const docRef = doc(db, "paidReport", docId);
    
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setReportData(docSnap.data() as ReportCardProps);
        } else {
          console.log("No such document!");
          toast.error("Report not found");
        }
        setIsLoadingReport(false);
      },
      (error) => {
        console.error("Error getting document:", error);
        toast.error("Error loading report");
        setIsLoadingReport(false);
      }
    );

    return () => unsubscribe();
  }, [docId]);

  // Fetch secure URL for the report
  useEffect(() => {
    if (!docId) return;

    let cancelled = false;

    const fetchSecureUrl = async () => {
      if (!auth.currentUser) return;
      try {
        setIsLoadingReport(true);
        const url = await downloadApi.getSecureDownloadUrl(docId, 'free');
        if (!cancelled) setSecureReportUrl(url);
      } catch (error) {
        console.error("Error fetching secure URL:", error);
        if (!cancelled) toast.error("Failed to load report. Please try again.");
      } finally {
        if (!cancelled) setIsLoadingReport(false);
      }
    };

    // Wait for Firebase auth to be ready before fetching. This handles the case
    // where the component mounts before auth.currentUser is hydrated (e.g. after
    // a hard reload), which previously left the iframe permanently empty.
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user && !cancelled) fetchSecureUrl();
    });

    // Refresh well before the 15-minute signed URL expires.
    // Bypass the in-memory cache so we actually get a fresh URL.
    const refreshInterval = setInterval(async () => {
      if (document.visibilityState === 'visible' && auth.currentUser) {
        downloadApi.clearUrlCache(docId);
        const url = await downloadApi.getSecureDownloadUrl(docId, 'free');
        if (url && !cancelled) setSecureReportUrl(url);
      }
    }, 10 * 60 * 1000);

    // Refresh when the tab becomes visible again — covers users who left
    // the tab open longer than the URL is valid for.
    const onVisibility = async () => {
      if (document.visibilityState === 'visible' && auth.currentUser) {
        downloadApi.clearUrlCache(docId);
        const url = await downloadApi.getSecureDownloadUrl(docId, 'free');
        if (url && !cancelled) setSecureReportUrl(url);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      unsubscribeAuth();
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [docId]);

  // Handle download button click
  const handleDownload = async () => {
    if (isLoadingDownload) return;
    
    setIsLoadingDownload(true);
    try {
      // Get a fresh download URL
      const url = await downloadApi.getSecureDownloadUrl(docId, 'free');
      
      if (url) {
        // Open in a new tab
        window.open(url, '_blank');
      } else {
        toast.error("Failed to generate download link");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Download failed. Please try again.");
    } finally {
      setIsLoadingDownload(false);
    }
  };

  const handleBackToMap = () => {
    navigate(`/map_page/${projectId}`);
  };

  return (
    <React.Fragment>
      <Navbar />

      <div className="centerpart mt-3">
        <Link className="dashboard" to="/dashboard">
          <IconWrapper Icon={GoArrowLeft} />
          {t('common:navigation.dashboard')}
        </Link>
        <p className="m-0">{t('unpaidProject:header.activeProject')} / {reportData?.projectName}</p>
      </div>
      
      <div className="my-3 d-flex align-items-end">
        <div 
          className="mx-3 d-flex align-items-center" 
          onClick={handleBackToMap}
          style={{ cursor: "pointer", width: "fit-content", position: "absolute" }}
        >
          <Image className="mb-3" src={images.leftArrow} alt="back_arrow" />
          <p className="d-none d-sm-inline mx-2 backToDashboard">
            {t('unpaidProject:backToMap')}
          </p>
        </div>
        <h1 className="newProjects mx-auto">{t('unpaidProject:header.projectOverview')}</h1>
      </div>
      
      <Container fluid className="mt-4 mb-5 py-3">
        <Row className="justify-content-center gap-3">
          <Col md={12} lg={5} style={{ width: "500px" }}>
            {isLoadingReport ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3">Loading report...</p>
              </div>
            ) : reportData ? (
              <>
                <div className="d-flex justify-content-between align-items-center PO-MYHOME">
                  <p className="mt-3 mb-0 unpaidtextOverFlow">
                    {reportData.projectName}
                  </p>
                  <p className="mt-3 mb-0">{t('unpaidProject:projectDetails.id')}: {docId}</p>
                </div>
                <hr />
                <div>
                  <div className="d-flex justify-content-between">
                    <p className="PO-date">{t('unpaidProject:projectDetails.date')}:</p>
                    <p className="POdata">{reportData.date}</p>
                  </div>
                  <div className="d-flex justify-content-between">
                    <p className="PO-date">{t('unpaidProject:projectDetails.pvAreas')}:</p>
                    <p className="POdata">
                      {reportData.verticalPVArea} {t('unpaidProject:projectDetails.vertical')},&nbsp;
                      {reportData.tiltedPVArea} {t('unpaidProject:projectDetails.tilted')}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between">
                    <p className="PO-date">{t('unpaidProject:projectDetails.detectionPoints')}:</p>
                    <p className="POdata">
                      {reportData.detectionPoints} {t('unpaidProject:projectDetails.detectionPointsLabel')}
                    </p>
                  </div>
                </div>
                
                {/* Download button with loading state */}
                <Button 
                  className="paid-downloadPDF" 
                  onClick={handleDownload}
                  disabled={isLoadingDownload}
                  style={{ display: 'block', width: '100%' }}
                >
                  {isLoadingDownload ? (
                    <>
                      <Spinner 
                        as="span" 
                        animation="border" 
                        size="sm" 
                        role="status" 
                        aria-hidden="true" 
                        className="me-2"
                      />
                      {t('common:loading')}
                    </>
                  ) : (
                    t('unpaidProject:buttons.downloadPdf')
                  )}
                </Button>
                
                <div className="d-flex justify-content-center mt-4">
                  {isLoadingReport ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </div>
                  ) : secureReportUrl ? (
                    <iframe
                      src={secureReportUrl}
                      style={{
                        width: `700px`,
                        height: "800px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                      }}
                      title="Report Preview"
                    />
                  ) : (
                    <div className="text-center py-5">
                      <p>Failed to load report preview. Please try refreshing the page.</p>
                      <Button 
                        variant="outline-primary" 
                        onClick={() => window.location.reload()}
                        className="mt-3"
                      >
                        Refresh Page
                      </Button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-5">
                <p>Report not found. Please check the project ID and try again.</p>
              </div>
            )}
          </Col>
          <Col md={12} lg={3}>
            <FullReport SimulationId={docId} />
          </Col>
        </Row>
      </Container>

      <Footer />
    </React.Fragment>
  );
};

export default UnpaidProject;